import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

const STATUS_CHIP = {
  SUCCEEDED: "bg-green-100 text-green-800",
  FAILED_SHALLOW: "bg-red-100 text-red-800",
  FAILED_TOO_LONG: "bg-red-200 text-red-900",
  FAILED_UNEXPECTED_FLOOD: "bg-orange-100 text-orange-800",
  ABORTED_FLOWERING: "bg-purple-100 text-purple-800",
  IN_PROGRESS: "bg-amber-100 text-amber-800",
};

const EVENT_CHIP = {
  DRYING_STARTED: "bg-gray-100 text-gray-700",
  THRESHOLD_REACHED: "bg-red-100 text-red-700",
  FIELD_FLOODED: "bg-blue-100 text-blue-700",
  UNEXPECTED_FLOOD: "bg-orange-100 text-orange-700",
  OVERDUE_ALERT: "bg-red-200 text-red-900",
  FLOWERING_SAFE_START: "bg-purple-100 text-purple-700",
  FLOWERING_SAFE_END: "bg-purple-100 text-purple-700",
  CYCLE_CLOSED_SHALLOW: "bg-yellow-100 text-yellow-800",
  MANUAL_LOG: "bg-gray-100 text-gray-600",
};

function EventRow({ event }) {
  return (
    <div className="flex items-start gap-2 py-1 text-xs">
      <span className="text-gray-400 whitespace-nowrap">
        {new Date(event.timestamp).toLocaleString()}
      </span>
      <span className={`px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${EVENT_CHIP[event.event_type] || "bg-gray-100 text-gray-700"}`}>
        {event.event_type.replace(/_/g, " ")}
      </span>
      {event.water_level_cm != null && (
        <span className="text-gray-500">{event.water_level_cm.toFixed(1)} cm</span>
      )}
      {event.notes && <span className="text-gray-400 italic">{event.notes}</span>}
      {event.triggered_by === "MANUAL" && (
        <span className="text-gray-400 text-xs">(manual)</span>
      )}
    </div>
  );
}

function CycleRow({ cycle, highlighted, onSelect }) {
  const [expanded, setExpanded] = useState(false);
  const chipClass = STATUS_CHIP[cycle.status] || "bg-gray-100 text-gray-700";

  return (
    <div className={`border-b border-gray-100 last:border-0 ${highlighted ? "bg-amber-50" : ""}`}>
      <div
        className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50"
        onClick={() => { setExpanded(e => !e); onSelect?.(cycle); }}
      >
        <span className="text-gray-400">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </span>

        {/* Date range */}
        <span className="text-sm text-gray-700 min-w-[180px]">
          {new Date(cycle.drying_started_at).toLocaleDateString()}
          {" → "}
          {cycle.drying_ended_at
            ? new Date(cycle.drying_ended_at).toLocaleDateString()
            : <span className="text-amber-600">ongoing</span>}
        </span>

        {/* Duration */}
        <span className="text-sm text-gray-500 w-20 text-right">
          {cycle.duration_days.toFixed(1)}d
        </span>

        {/* Min depth */}
        <span className="text-sm text-gray-500 w-24 text-right">
          {cycle.min_water_level_cm != null
            ? `${cycle.min_water_level_cm.toFixed(1)} cm`
            : "—"}
        </span>

        {/* Status chip */}
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${chipClass} flex-1`}>
          {cycle.status.replace(/_/g, " ")}
        </span>
      </div>

      {/* Expanded: reason + events */}
      {expanded && (
        <div className="px-8 pb-3">
          {cycle.status_reason && (
            <p className="text-xs text-gray-500 italic mb-2">{cycle.status_reason}</p>
          )}
          {cycle.events && cycle.events.length > 0 ? (
            <div className="divide-y divide-gray-50">
              {cycle.events.map(e => <EventRow key={e.id} event={e} />)}
            </div>
          ) : (
            <p className="text-xs text-gray-400">No events loaded. Click the cycle to fetch details.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function AWDCycleList({ cycles = [], highlightedCycleId, onCycleSelect }) {
  if (cycles.length === 0) {
    return (
      <div className="text-center text-sm text-gray-400 py-8">
        No AWD cycles recorded yet.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Table header */}
      <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500">
        <span className="w-4" />
        <span className="min-w-[180px]">Date Range</span>
        <span className="w-20 text-right">Duration</span>
        <span className="w-24 text-right">Min Depth</span>
        <span className="flex-1">Status</span>
      </div>

      {cycles.map(cycle => (
        <CycleRow
          key={cycle.id}
          cycle={cycle}
          highlighted={cycle.id === highlightedCycleId}
          onSelect={onCycleSelect}
        />
      ))}
    </div>
  );
}
